import { SQLocalDrizzle } from "sqlocal/drizzle";
import { drizzle } from "drizzle-orm/sqlite-proxy";
import * as schema from "../../shared/src/schema";
import { captureError } from "./sentry";

// ── Global SQLite error reporter ─────────────────────────────────────
// Any module can call reportDbError(err) to surface a SQLite error in
// the UI.  The overlay in app.tsx subscribes via onDbError().
let _dbError: string | null = null;
const _dbErrorListeners = new Set<() => void>();
export function reportDbError(err: unknown) {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg === _dbError) return; // dedupe
  captureError(err, { source: "sqlite" });
  _dbError = msg;
  for (const fn of _dbErrorListeners) fn();
}
export function getDbError() {
  return _dbError;
}
export function clearDbError() {
  _dbError = null;
  for (const fn of _dbErrorListeners) fn();
}
export function onDbError(cb: () => void) {
  _dbErrorListeners.add(cb);
  return () => {
    _dbErrorListeners.delete(cb);
  };
}

// Lazy initialization - created per-user after auth
let sqlocal: SQLocalDrizzle | null = null;
let _db: ReturnType<typeof drizzle<typeof schema>> | null = null;
let currentUserId: string | null = null;

// Getter for db - throws if not initialized
export function getDb() {
  if (!_db)
    throw new Error("Database not initialized. Call initClientDatabase first.");
  return _db;
}

// Proxy export for backwards compatibility
// Modules importing 'db' directly will get the lazy proxy
export const db = new Proxy({} as ReturnType<typeof drizzle<typeof schema>>, {
  get(_target, prop) {
    return getDb()[prop as keyof typeof _db];
  },
});

// Get sqlocal instance for raw SQL
export function getSqlocal() {
  if (!sqlocal)
    throw new Error("Database not initialized. Call initClientDatabase first.");
  return sqlocal;
}

// Create tables on init
export async function initClientDatabase(userId: string) {
  // If switching users, close old connection
  if (sqlocal && currentUserId !== userId) {
    console.log(
      "[DB] Switching user database from",
      currentUserId?.slice(0, 8),
      "to",
      userId.slice(0, 8),
    );
    try {
      await sqlocal.destroy();
    } catch (e) {
      console.warn("[DB] Error closing old database:", e);
    }
    sqlocal = null;
    _db = null;
  }

  // Already initialized for this user
  if (sqlocal && currentUserId === userId) {
    console.log(
      "[DB] Database already initialized for user",
      userId.slice(0, 8),
    );
    return;
  }

  // Create per-user database
  const dbName = `biostream-${userId.slice(0, 8)}.sqlite3`;
  console.log("[DB] Initializing database:", dbName);
  sqlocal = new SQLocalDrizzle(dbName);
  _db = drizzle(sqlocal.driver, sqlocal.batchDriver, { schema });
  currentUserId = userId;

  // Migration: drop old events table if it has sequence column (schema v1 -> v2)
  try {
    const cols = await sqlocal.sql`PRAGMA table_info(events)`;
    const hasSequence = (cols as { name: string }[]).some(
      (c) => c.name === "sequence",
    );
    if (hasSequence) {
      console.log("[DB] Migrating events table: dropping sequence column");
      await sqlocal.sql`DROP TABLE IF EXISTS events`;
    }
  } catch {
    /* table doesn't exist yet */
  }

  await sqlocal.sql`
    CREATE TABLE IF NOT EXISTS events (
      id TEXT PRIMARY KEY,
      type TEXT NOT NULL,
      entity_id TEXT NOT NULL,
      payload TEXT NOT NULL,
      user_id TEXT NOT NULL,
      session_id TEXT NOT NULL,
      client_timestamp INTEGER NOT NULL,
      server_timestamp INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE INDEX IF NOT EXISTS idx_events_server_ts ON events(server_timestamp)`;
  await sqlocal.sql`CREATE INDEX IF NOT EXISTS idx_events_entity ON events(entity_id, type, client_timestamp)`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS regions (
      id TEXT PRIMARY KEY, name TEXT, archived INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS hospitals (
      id TEXT PRIMARY KEY, name TEXT, region_id TEXT, archived INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS units (
      id TEXT PRIMARY KEY, name TEXT, merged_into TEXT, archived INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS users (
      id TEXT PRIMARY KEY, email TEXT NOT NULL UNIQUE, name TEXT, picture TEXT,
      role TEXT NOT NULL DEFAULT 'medic', updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS user_hospitals (
      user_id TEXT NOT NULL, hospital_id TEXT NOT NULL, PRIMARY KEY (user_id, hospital_id)
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS user_units (
      user_id TEXT NOT NULL, unit_id TEXT NOT NULL, PRIMARY KEY (user_id, unit_id)
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS user_regions (
      user_id TEXT NOT NULL, region_id TEXT NOT NULL, PRIMARY KEY (user_id, region_id)
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS unit_hierarchy (
      parent_unit_id TEXT NOT NULL, child_unit_id TEXT NOT NULL,
      PRIMARY KEY (parent_unit_id, child_unit_id)
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS sessions (
      id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
      destroyed INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS patients (
      id TEXT PRIMARY KEY, unit_id TEXT, merged_into TEXT, id_number TEXT, name TEXT, date_of_birth TEXT, sex TEXT,
      blood_type TEXT, rh_factor TEXT, allergy_status TEXT, allergy_details TEXT, military_rank TEXT,
      personal_data_erased INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS cases (
      id TEXT PRIMARY KEY, patient_id TEXT NOT NULL, unit_id TEXT, number TEXT, hospital_id TEXT,
      event_time INTEGER, event_location TEXT, arrival_time INTEGER, triage_category TEXT,
      role TEXT, medical_facility TEXT, transport_type TEXT, trauma_types TEXT,
      trauma_penetrating INTEGER, trauma_non_penetrating INTEGER, trauma_locations TEXT, condition_types TEXT, cbrn TEXT,
      prior_care TEXT, tetanus_prophylaxis TEXT,
      r1_care TEXT, r1_kit1 TEXT, r1_kit2 TEXT,
      r2_arrival_time INTEGER, r2_triage_category TEXT, r2_hospital_id TEXT,
      r2_care TEXT, r2_death_time INTEGER, r2_death_circumstances TEXT,
      r2_discharge_time INTEGER, r2_discharge_destination TEXT, r2_evacuation_category TEXT,
      r2_discharge_transport_type TEXT, r2_discharge_position TEXT,
      r2_escort_doctor INTEGER, r2_escort_nurse INTEGER, r2_discharge_role TEXT, r2_discharge_vehicle_type TEXT, r2_discharge_equipment TEXT,
      r2_tetanus_prophylaxis TEXT, r2_kit1 TEXT, r2_kit2 TEXT,
      r2_diagnosis TEXT, r2_notes TEXT, r2_medic_name TEXT, r2_medic_facility TEXT, r2_medic_role TEXT,
      r1_prior_care_status TEXT,
      death_time INTEGER, death_circumstances TEXT,
      discharge_time INTEGER, discharge_destination TEXT, evacuation_category TEXT,
      discharge_transport_type TEXT, discharge_position TEXT, escort_doctor INTEGER,
      escort_nurse INTEGER, discharge_role TEXT, discharge_vehicle_type TEXT, discharge_equipment TEXT,
      diagnosis TEXT, notes TEXT, medic_name TEXT, medic_facility TEXT,
      medic_role TEXT, created_by TEXT, archived INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_vitals (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER, time INTEGER,
      respiratory_rate INTEGER, sp_o2 INTEGER, pulse INTEGER,
      systolic INTEGER, diastolic INTEGER, gcs_eye INTEGER, gcs_verbal INTEGER,
      gcs_motor INTEGER, pain_scale INTEGER, temperature TEXT, diuresis INTEGER,
      updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_ventilation (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER, time INTEGER,
      mode TEXT, rate INTEGER, tidal_volume INTEGER, fi_o2 INTEGER,
      peep INTEGER, pip INTEGER, et_co2 INTEGER, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_diagnostics (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER, time INTEGER,
      e_fast TEXT, xray TEXT, ct TEXT, ecg TEXT, ultrasound TEXT,
      otoscopy_left TEXT, otoscopy_right TEXT, pupils_left TEXT, pupils_right TEXT,
      other TEXT, notes TEXT, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_lab_values (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER, time INTEGER,
      hg TEXT, hct TEXT, rbc TEXT, wbc TEXT, platelets TEXT,
      na TEXT, k TEXT, ca TEXT, cl TEXT, lactate TEXT,
      urea TEXT, creatinine TEXT, glucose TEXT,
      ph TEXT, p_co2 REAL, p_o2 REAL, h_co3 TEXT,
      notes TEXT, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_exam (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER, time INTEGER,
      airway_status TEXT, advanced_airway_type TEXT, respiratory_support_type TEXT, breathing_sounds TEXT,
      skin_color TEXT, skin_moisture INTEGER, skin_temperature TEXT,
      skin_edema INTEGER, skin_rash INTEGER,
      skin_pink INTEGER, skin_pale INTEGER, skin_cyanotic INTEGER, skin_dry INTEGER, skin_moist INTEGER,
      chest_symmetric INTEGER, chest_asym_right INTEGER, chest_asym_left INTEGER,
      chest_flail_left INTEGER, chest_flail_right INTEGER,
      chest_penetrating_left INTEGER, chest_penetrating_right INTEGER,
      heart_sounds_clear INTEGER, trachea_midline INTEGER,
      trachea_deviation_right INTEGER, trachea_deviation_left INTEGER,
      peripheral_pulse TEXT,
      capillary_refill_over_2s INTEGER, jugular_veins_collapsed INTEGER, jugular_veins_distended INTEGER,
      abdomen_distended INTEGER, abdomen_painful INTEGER, peritoneal_irritation INTEGER,
      abdomen_penetrating_trauma INTEGER, pelvis_stable INTEGER,
      limb_deformation TEXT, neuro_deficit TEXT,
      deformation_any INTEGER, neuro_deficit_any INTEGER,
      pupils_equal INTEGER,
      pupils_light_reaction INTEGER, pupils_light_reaction_left INTEGER, pupils_light_reaction_right INTEGER,
      vision_left TEXT, vision_right TEXT,
      skull_fracture INTEGER, csf_leak INTEGER, cervical_spine_injury INTEGER,
      gcs_total INTEGER, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_cpr (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER,
      start_time INTEGER, end_time INTEGER, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_tourniquet (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER,
      limb TEXT, application_time INTEGER, removal_time INTEGER,
      conversion_time INTEGER, repositioning_time INTEGER, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_medication (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER, time INTEGER,
      name TEXT, dose TEXT, dose_unit TEXT, route TEXT, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_surgery (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER,
      start_time INTEGER, end_time INTEGER, description TEXT, surgery_types TEXT,
      anesthesia_local INTEGER, anesthesia_regional INTEGER,
      anesthesia_general INTEGER, anesthesia_sedation INTEGER,
      updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS attachments (
      id TEXT PRIMARY KEY, case_id TEXT NOT NULL, mime_type TEXT, captured_at INTEGER,
      sha256 TEXT, caption TEXT, deleted INTEGER NOT NULL DEFAULT 0, updated_at INTEGER NOT NULL
    )`;
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS pending_commands (
      id TEXT PRIMARY KEY, session_id TEXT NOT NULL, envelope TEXT NOT NULL, created_at INTEGER NOT NULL
    )`;
  // Migration: add session_id column if it doesn't exist (for older databases)
  try {
    await sqlocal.sql`ALTER TABLE pending_commands ADD COLUMN session_id TEXT NOT NULL DEFAULT ''`;
  } catch {
    /* already exists */
  }

  // Migration: add role column (replaces is_admin + is_medic)
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN role TEXT NOT NULL DEFAULT 'medic'`;
  } catch {
    /* already exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN created_by TEXT`;
  } catch {
    /* already exists */
  }

  // Migration: add user activity columns (login timestamps + invite tracking)
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN first_login_at INTEGER`;
  } catch {
    /* already exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN last_login_at INTEGER`;
  } catch {
    /* already exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN invite_sent_at INTEGER`;
  } catch {
    /* already exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN dashboards TEXT`;
  } catch {
    /* already exists */
  }

  // v3 migrations: PDF form model
  try {
    await sqlocal.sql`ALTER TABLE patients ADD COLUMN id_number TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r1_care TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r1_kit1 TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r1_kit2 TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_arrival_time INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_triage_category TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_hospital_id TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_care TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_death_time INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_death_circumstances TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_time INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_destination TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_evacuation_category TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_transport_type TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_position TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_escort_doctor INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_escort_nurse INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_role TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_equipment TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_diagnosis TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_notes TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_medic_name TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_medic_facility TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_medic_role TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN discharge_role TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE patients ADD COLUMN allergy_status TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE patients ADD COLUMN allergy_details TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE patients ADD COLUMN patient_category TEXT`;
  } catch {
    /* exists */
  }
  // Add role column to time-series tables
  for (const t of [
    "case_vitals",
    "case_ventilation",
    "case_diagnostics",
    "case_lab_values",
    "case_exam",
    "case_cpr",
    "case_medication",
    "case_surgery",
  ]) {
    try {
      await sqlocal.sql`ALTER TABLE ${t} ADD COLUMN role INTEGER`;
    } catch {
      /* exists */
    }
  }
  // Create tourniquet table (migration from treatment)
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS case_tourniquet (
    id TEXT PRIMARY KEY, case_id TEXT NOT NULL, role INTEGER,
    limb TEXT, application_time INTEGER, removal_time INTEGER,
    conversion_time INTEGER, repositioning_time INTEGER, updated_at INTEGER NOT NULL
  )`;
  // v4 migrations: tourniquet conversion/repositioning + R2 tetanus/kits
  try {
    await sqlocal.sql`ALTER TABLE case_tourniquet ADD COLUMN conversion_time INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE case_tourniquet ADD COLUMN repositioning_time INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_tetanus_prophylaxis TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_kit1 TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_kit2 TEXT`;
  } catch {
    /* exists */
  }
  // v5 migrations: vehicle type + prior care status
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN discharge_vehicle_type TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_discharge_vehicle_type TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r1_prior_care_status TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN prior_care_time INTEGER`;
  } catch {
    /* exists */
  }
  // v6 migrations: diagnostics/lab notes, exam respiratory_support_type
  try {
    await sqlocal.sql`ALTER TABLE case_diagnostics ADD COLUMN notes TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE case_lab_values ADD COLUMN notes TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN respiratory_support_type TEXT`;
  } catch {
    /* exists */
  }
  // v7 migrations: diagnostics other field
  try {
    await sqlocal.sql`ALTER TABLE case_diagnostics ADD COLUMN other TEXT`;
  } catch {
    /* exists */
  }
  // v8 migrations: jugular veins collapsed (decoupled from distended)
  try {
    await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN jugular_veins_collapsed INTEGER`;
  } catch {
    /* exists */
  }
  // v9 migrations: separate left/right pupil light reaction
  try {
    await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN pupils_light_reaction_left INTEGER`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN pupils_light_reaction_right INTEGER`;
  } catch {
    /* exists */
  }
  // v10 migrations: unit merge
  try {
    await sqlocal.sql`ALTER TABLE units ADD COLUMN merged_into TEXT`;
  } catch {
    /* exists */
  }
  // v11 migrations: signature fields
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN signature_sha256 TEXT`;
  } catch {
    /* exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN r2_signature_sha256 TEXT`;
  } catch {
    /* exists */
  }
  // v12 migrations: independent trauma penetrating
  try {
    await sqlocal.sql`ALTER TABLE cases ADD COLUMN trauma_non_penetrating INTEGER`;
  } catch {
    /* exists */
  }
  // v13 migrations: independent skin checkboxes
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN skin_pink INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN skin_pale INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN skin_cyanotic INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN skin_dry INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN skin_moist INTEGER`; } catch { /* exists */ }
  // v14 migrations: trachea deviation direction
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN trachea_deviation_right INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN trachea_deviation_left INTEGER`; } catch { /* exists */ }
  // v15 migrations: independent chest asymmetry R/L
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN chest_asym_right INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN chest_asym_left INTEGER`; } catch { /* exists */ }
  // v16 migrations: limb deformation/neuro deficit Ні/Так
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN deformation_any INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_exam ADD COLUMN neuro_deficit_any INTEGER`; } catch { /* exists */ }
  // v20 migrations: structured discharge destination fields
  try { await sqlocal.sql`ALTER TABLE cases ADD COLUMN discharge_hospital_id TEXT`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE cases ADD COLUMN discharge_unit_id TEXT`; } catch { /* exists */ }
  // v17 migrations: anesthesia type → individual boolean columns
  try { await sqlocal.sql`ALTER TABLE case_surgery ADD COLUMN anesthesia_local INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_surgery ADD COLUMN anesthesia_regional INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_surgery ADD COLUMN anesthesia_general INTEGER`; } catch { /* exists */ }
  try { await sqlocal.sql`ALTER TABLE case_surgery ADD COLUMN anesthesia_sedation INTEGER`; } catch { /* exists */ }
  // v23 migration: transport type → JSON array
  await sqlocal.sql`UPDATE cases SET transport_type = json_array(transport_type) WHERE transport_type IS NOT NULL AND transport_type NOT LIKE '[%'`;
  await sqlocal.sql`UPDATE cases SET discharge_transport_type = json_array(discharge_transport_type) WHERE discharge_transport_type IS NOT NULL AND discharge_transport_type NOT LIKE '[%'`;
  await sqlocal.sql`UPDATE cases SET r2_discharge_transport_type = json_array(r2_discharge_transport_type) WHERE r2_discharge_transport_type IS NOT NULL AND r2_discharge_transport_type NOT LIKE '[%'`;
  // v22 migration: patient archive
  try { await sqlocal.sql`ALTER TABLE patients ADD COLUMN archived INTEGER NOT NULL DEFAULT 0`; } catch { /* exists */ }
  // v21 migration: hospital merge
  try { await sqlocal.sql`ALTER TABLE hospitals ADD COLUMN merged_into TEXT`; } catch { /* exists */ }

  // Broadcast messages: per-user permission flags + (server-side) message log
  // The messages table on the client mirrors the schema for type-safety, but
  // is unused — the inbox is served by the listMyMessages RPC. Live message
  // events still arrive as WS pushes for toast notifications.
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN can_broadcast_to_own_hospital INTEGER NOT NULL DEFAULT 0`;
  } catch {
    /* already exists */
  }
  try {
    await sqlocal.sql`ALTER TABLE users ADD COLUMN can_broadcast_to_own_unit INTEGER NOT NULL DEFAULT 0`;
  } catch {
    /* already exists */
  }
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS messages (
      id TEXT PRIMARY KEY,
      sender_id TEXT NOT NULL,
      audience_type TEXT NOT NULL,
      audience_hospital_id TEXT,
      audience_unit_ids TEXT,
      body TEXT NOT NULL,
      created_at INTEGER NOT NULL,
      updated_at INTEGER NOT NULL
    )`;

  // Blob upload queue - tracks blobs stored in OPFS awaiting upload
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS pending_blobs (
      sha256 TEXT PRIMARY KEY,
      mime_type TEXT NOT NULL,
      size INTEGER NOT NULL,
      created_at INTEGER NOT NULL,
      upload_attempts INTEGER NOT NULL DEFAULT 0,
      last_attempt_at INTEGER,
      error TEXT
    )`;

  // Track which cases are "pinned" locally (last 30 + user-created)
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS local_cases (
      case_id TEXT PRIMARY KEY,
      pinned INTEGER NOT NULL DEFAULT 0,
      accessed_at INTEGER NOT NULL
    )`;

  // Denormalized entity hashes for fast Merkle sync
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS entity_hashes (
      entity_id TEXT PRIMARY KEY,
      hash TEXT NOT NULL
    )`;

  // User preferences (client-only, for column settings, etc.)
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS user_preferences (
      key TEXT PRIMARY KEY,
      value TEXT NOT NULL
    )`;

  // Medic unit usage projection (sorted dropdown for facility picker)
  await sqlocal.sql`CREATE TABLE IF NOT EXISTS medic_unit_usage (
      user_id TEXT NOT NULL,
      unit_id TEXT NOT NULL,
      last_used_at INTEGER NOT NULL,
      PRIMARY KEY (user_id, unit_id)
    )`;

  // v24 migration: hypothermiaType → hypothermiaActive/hypothermiaPassive
  await sqlocal.sql`UPDATE cases SET r2_care = json_set(
    json_remove(r2_care, '$.hypothermiaType'),
    '$.hypothermiaActive', json('true')
  ) WHERE json_extract(r2_care, '$.hypothermiaType') = 'active'`;
  await sqlocal.sql`UPDATE cases SET r2_care = json_set(
    json_remove(r2_care, '$.hypothermiaType'),
    '$.hypothermiaPassive', json('true')
  ) WHERE json_extract(r2_care, '$.hypothermiaType') = 'passive'`;

  // Initialize case retention from existing cases
  const { initCaseRetention } = await import("./retention");
  await initCaseRetention(sqlocal);
}

// ─── User preferences helpers ─────────────────────────────────────────

export async function getPreference<T>(key: string): Promise<T | null> {
  const sql = getSqlocal();
  const result =
    await sql.sql`SELECT value FROM user_preferences WHERE key = ${key}`;
  if (result.length === 0) return null;
  try {
    return JSON.parse(result[0].value as string) as T;
  } catch {
    return null;
  }
}

export async function setPreference<T>(key: string, value: T): Promise<void> {
  const sql = getSqlocal();
  const json = JSON.stringify(value);
  await sql.sql`INSERT OR REPLACE INTO user_preferences (key, value) VALUES (${key}, ${json})`;
}

/**
 * Clear all client data and force a full re-sync.
 * Call this when switching users or after server database reset.
 */
export async function clearClientDatabase(): Promise<void> {
  if (!sqlocal) {
    console.log("[DB] No database to clear");
    return;
  }
  // Drop all data tables (keep structure)
  await sqlocal.sql`DELETE FROM events`;
  await sqlocal.sql`DELETE FROM regions`;
  await sqlocal.sql`DELETE FROM hospitals`;
  await sqlocal.sql`DELETE FROM units`;
  await sqlocal.sql`DELETE FROM users`;
  await sqlocal.sql`DELETE FROM user_hospitals`;
  await sqlocal.sql`DELETE FROM user_units`;
  await sqlocal.sql`DELETE FROM user_regions`;
  await sqlocal.sql`DELETE FROM unit_hierarchy`;
  await sqlocal.sql`DELETE FROM sessions`;
  await sqlocal.sql`DELETE FROM patients`;
  await sqlocal.sql`DELETE FROM cases`;
  await sqlocal.sql`DELETE FROM case_vitals`;
  await sqlocal.sql`DELETE FROM case_ventilation`;
  await sqlocal.sql`DELETE FROM case_diagnostics`;
  await sqlocal.sql`DELETE FROM case_lab_values`;
  await sqlocal.sql`DELETE FROM case_exam`;
  await sqlocal.sql`DELETE FROM case_cpr`;
  await sqlocal.sql`DELETE FROM case_tourniquet`;
  await sqlocal.sql`DELETE FROM case_medication`;
  await sqlocal.sql`DELETE FROM case_surgery`;
  await sqlocal.sql`DELETE FROM attachments`;
  await sqlocal.sql`DELETE FROM messages`;
  await sqlocal.sql`DELETE FROM pending_commands`;
  await sqlocal.sql`DELETE FROM pending_blobs`;
  await sqlocal.sql`DELETE FROM local_cases`;
  await sqlocal.sql`DELETE FROM entity_hashes`;
  console.log("[DB] Client database cleared");
}

/**
 * Destroy the database connection (for logout).
 */
export async function destroyDatabase(): Promise<void> {
  if (sqlocal) {
    await clearClientDatabase();
    try {
      await sqlocal.destroy();
    } catch (e) {
      console.warn("[DB] Error destroying database:", e);
    }
    sqlocal = null;
    _db = null;
    currentUserId = null;
    console.log("[DB] Database destroyed");
  }
}

// Re-export sqlocal for legacy code that expects it
export { sqlocal };
